import React, { useState } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { PG } from './theme';

const preSx = {
  m: 0,
  mb: 1,
  p: 1.25,
  overflow: 'auto',
  maxHeight: 280,
  bgcolor: '#0a0f1a',
  borderRadius: 1,
  fontSize: '0.75rem',
  lineHeight: 1.45,
  color: '#dbe7ff',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
};

const mdSx = {
  color: PG.text,
  '& p': { m: 0, mb: 1, fontSize: '0.9rem', lineHeight: 1.55, color: PG.text },
  '& p:last-child': { mb: 0 },
  '& h1, & h2, & h3': { fontSize: '1.05rem', fontWeight: 650, mt: 1.25, mb: 0.75, lineHeight: 1.3, color: PG.text },
  '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
  '& li': { fontSize: '0.9rem', mb: 0.25, color: PG.text },
  '& pre': preSx,
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.8em',
    bgcolor: 'rgba(56,189,248,0.12)',
    px: 0.5,
    borderRadius: 0.5,
    color: '#e8eefc',
  },
  '& pre code': { bgcolor: 'transparent', p: 0, fontSize: '0.75rem' },
  '& img': { maxWidth: '100%', borderRadius: 1, display: 'block', my: 1 },
  '& blockquote': {
    m: 0,
    mb: 1,
    pl: 1.5,
    borderLeft: '3px solid #334155',
    color: PG.muted,
  },
  '& a': { wordBreak: 'break-all', color: PG.accent },
  '& table': { fontSize: '0.8rem', borderCollapse: 'collapse', mb: 1, width: '100%' },
  '& th, & td': { border: '1px solid #243044', p: 0.75, textAlign: 'left' },
};

function nodeText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (typeof node === 'object' && 'props' in node) {
    return nodeText((node as React.ReactElement<{ children?: React.ReactNode }>).props.children);
  }
  return '';
}

const CollapsibleFence: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const [open, setOpen] = useState(false);
  const text = nodeText(children);
  const lines = text.split('\n').length;
  const lang = (className || '').replace(/^language-/, '').trim();
  if (lines < 16) {
    return (
      <Box component="pre" className={className} sx={preSx}>
        {children}
      </Box>
    );
  }
  return (
    <Box sx={{ mb: 1 }}>
      <Button size="small" onClick={() => setOpen((v) => !v)} sx={{ color: PG.accent, textTransform: 'none', px: 0 }}>
        {open ? 'Hide' : 'Show'} {lang || 'file'} · {lines} lines
      </Button>
      {open && (
        <Box component="pre" className={className} sx={preSx}>
          {children}
        </Box>
      )}
    </Box>
  );
};

function splitProseAndDump(text: string): { prose: string; dump: string } {
  const src = text || '';
  const htmlAt = src.search(/<!DOCTYPE html\b|<html[\s>]/i);
  if (htmlAt > 0) {
    return { prose: src.slice(0, htmlAt).trim(), dump: src.slice(htmlAt).trim() };
  }
  if (htmlAt === 0) {
    return { prose: '', dump: src.trim() };
  }
  return { prose: src, dump: '' };
}

const MarkdownBody: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={mdSx}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize]}
      components={{
        p: ({ children }: { children?: React.ReactNode }) => (
          <Typography variant="body2" sx={{ color: 'inherit' }}>{children}</Typography>
        ),
        a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
          <Link href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </Link>
        ),
        img: ({ src, alt }: { src?: string; alt?: string }) => (
          <Box
            component="img"
            src={src}
            alt={alt || ''}
            sx={{ maxWidth: '100%', borderRadius: 1, my: 1 }}
          />
        ),
        pre: ({ children }: { children?: React.ReactNode }) => {
          let lang = '';
          React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && (child.props as { className?: string })?.className) {
              lang = String((child.props as { className?: string }).className).replace(/^language-/, '');
            }
          });
          return <CollapsibleFence className={lang}>{children}</CollapsibleFence>;
        },
      }}
    >
      {text || ''}
    </ReactMarkdown>
  </Box>
);

export const ChatMarkdown: React.FC<{ text: string; streaming?: boolean }> = ({ text, streaming }) => {
  const [showDump, setShowDump] = useState(false);
  // While tokens arrive, keep the full reply visible. Collapsing fences / "Show source"
  // on every closed ``` made App Builder flicker between a code dump and a minimized chip.
  if (streaming) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.55, color: PG.text }}>
        {text}
      </Typography>
    );
  }
  const { prose, dump } = splitProseAndDump(text);
  return (
    <Box>
      {prose ? <MarkdownBody text={prose} /> : null}
      {dump ? (
        <Box sx={{ mt: prose ? 1 : 0 }}>
          {!prose && (
            <Typography variant="body2" sx={{ color: PG.text, mb: 0.75 }}>
              Generated app files are on the canvas. You can also inspect the source:
            </Typography>
          )}
          <Button size="small" onClick={() => setShowDump((v) => !v)} sx={{ color: PG.accent, textTransform: 'none' }}>
            {showDump ? 'Hide generated source' : 'Show generated source (also in the canvas)'}
          </Button>
          {showDump && (
            <Box component="pre" sx={{ ...preSx, mt: 1 }}>
              {dump}
            </Box>
          )}
        </Box>
      ) : null}
      {!prose && !dump ? (
        <Typography variant="body2" sx={{ color: PG.muted, fontStyle: 'italic' }}>
          This reply was not stored. New messages are saved to this chat’s history.
        </Typography>
      ) : null}
    </Box>
  );
};
